import { useState } from 'react';
import type { Receipt } from '@salonomy/types';
import { Button, Pill } from '@salonomy/ui';
import { fetchThermalReceipt, useEmailReceipt } from '../../data/receipts.js';
import { ApiClientError } from '../../lib/api.js';
import { useToast } from '../../lib/toast.js';
import './till.css';

/**
 * WS-MONEY — after a sale settles and a receipt is ISSUED, this surfaces the server-assigned receipt
 * number and offers Print (thermal) + Email. Every figure here is the server's: the receipt number,
 * ref and gross total all come back from the issue call; this component only displays the issued
 * `receipt` and wires the two actions to the data hooks.
 *
 * Print renders the receipt server-side as ESC/POS bytes (`fetchThermalReceipt`) — the bytes are
 * produced fresh per print and handed to `onPrint` so the integrator can drive the physical Star/Epson
 * printer (or a WebUSB/agent bridge). With no `onPrint` wired we still fetch the bytes (proving the
 * render path works) and toast success. Email enqueues a send job, idempotent on a per-send key minted
 * once; both buttons gate on `isPending` so a double-tap can't fire twice. 422/409 surface faithfully.
 */
export function ReceiptActions({
  receipt,
  onPrint,
}: {
  receipt: Receipt;
  /**
   * Optional sink for the rendered thermal bytes (base64 ESC/POS) — the integrator drives the
   * physical printer here. When omitted, Print still fetches the bytes and reports success.
   */
  onPrint?: (escposBase64: string) => void | Promise<void>;
}) {
  const toast = useToast();
  const email = useEmailReceipt();
  const [printing, setPrinting] = useState(false);

  async function print() {
    setPrinting(true);
    try {
      const thermal = await fetchThermalReceipt(receipt.id, { openDrawer: true });
      await onPrint?.(thermal.escposBase64);
      toast.success(`Receipt ${receipt.receiptRef} sent to printer`);
    } catch (err) {
      toast.error(
        err instanceof ApiClientError ? err.message : 'Could not render the receipt for printing',
      );
    } finally {
      setPrinting(false);
    }
  }

  async function sendEmail() {
    try {
      // Mint the idempotency key once per send; the button is disabled while pending so a double-tap
      // can't reach here a second time, and the API is idempotent on this key.
      await email.mutateAsync({ id: receipt.id, idempotencyKey: crypto.randomUUID() });
      toast.success(
        receipt.clientEmail ? `Receipt emailed to ${receipt.clientEmail}` : 'Receipt email queued',
      );
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not email the receipt');
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Pill tone="success">Receipt {receipt.receiptRef}</Pill>
        {receipt.status === 'voided' ? <Pill tone="danger">voided</Pill> : null}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button
          variant="outline"
          onClick={print}
          disabled={printing || receipt.status === 'voided'}
        >
          {printing ? 'Printing…' : 'Print receipt'}
        </Button>
        <Button
          variant="outline"
          onClick={sendEmail}
          disabled={email.isPending || receipt.status === 'voided'}
        >
          {email.isPending ? 'Emailing…' : 'Email receipt'}
        </Button>
      </div>
    </div>
  );
}
